import React, { useEffect, useState, useRef, useCallback } from "react";
import { apiRequest } from "../utils/api";
import "../styles/MailList.css";

const MailList = ({
  onSelect,
  currentFolder,
  onReload,
  emails,
  setEmails,
  isSentList = false,
  isDraftList = false,
  selectedEmailId,
}) => {
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState("received_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterText, setFilterText] = useState("");

  const observerRef = useRef();

  // フォルダ切り替え時に sortKey を修正（draft は created_at）
  useEffect(() => {
    setSortKey(isDraftList ? "created_at" : "received_at");
  }, [isDraftList]);

  const fetchEmails = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);

    const baseQuery = `page=${page}&page_size=10&search=${encodeURIComponent(
      filterText
    )}&ordering=${sortOrder === "asc" ? "" : "-"}${sortKey}`;

    let apiUrl = "";

    if (isDraftList) {
      apiUrl = `inventory/email_drafts/?${baseQuery}`;
    } else if (isSentList) {
      apiUrl = `inventory/mail/sent/?${baseQuery}`;
    } else {
      const folderQuery =
        currentFolder === "trash"
          ? "&deleted=true"
          : `&folder=${encodeURIComponent(currentFolder)}`;
      apiUrl = `inventory/mail/received/?${baseQuery}${folderQuery}`;
    }

    try {
      const res = await apiRequest(apiUrl);
      
      if (res && Array.isArray(res.results)) {
        setEmails((prev) => [...prev, ...res.results]);
        setHasMore(!!res.next);
        setPage((prev) => prev + 1);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("メール取得エラー:", err);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [page, filterText, sortKey, sortOrder, currentFolder, loading, hasMore, isSentList, isDraftList, setEmails,]);

  useEffect(() => {
    setEmails([]);
    setPage(1);
    setHasMore(true);
  }, [currentFolder, filterText, sortKey, sortOrder, isSentList, isDraftList, setEmails]);

  useEffect(() => {
    if (page === 1) {
      fetchEmails();
    }
  }, [page, fetchEmails]);

  const lastEmailRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchEmails();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, fetchEmails]
  );

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  return (
    <div className="mail-list">
      <div className="MailList-controls">
        <input
          type="text"
          placeholder="件名・送信者・本文で検索"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="MailList-filterInput"
        />
        <div className="MailList-headerRow">
          <div onClick={() => toggleSort("subject")}>
            件名 {sortKey === "subject" && (sortOrder === "asc" ? "▲" : "▼")}
          </div>
          <div onClick={() => toggleSort(isSentList ? "to_address" : "from_address")}>
            {isSentList ? "宛先" : "送信者"}{" "}
            {sortKey === (isSentList ? "to_address" : "from_address") && (sortOrder === "asc" ? "▲" : "▼")}
          </div>
          <div onClick={() => toggleSort(isDraftList ? "created_at" : "received_at")}>
            日時 {sortKey === (isDraftList ? "created_at" : "received_at") && (sortOrder === "asc" ? "▲" : "▼")}
          </div>
        </div>
      </div>

      <div className="MailList-scrollArea">
        <ul className="MailList-list">
          {emails.map((email, index) => {
            const isLast = index === emails.length - 1;
            const isSelected = email.id === selectedEmailId;

            return (
              <li
                key={email.id}
                ref={isLast ? lastEmailRef : null}
                className={`MailList-item ${
                  isDraftList
                    ? "MailList-draft"
                    : email.is_read
                    ? "MailList-read"
                    : "MailList-unread"
                } ${isSelected ? "MailList-selected" : ""}`}
                onClick={() => {
                  onSelect(email.id);
                  if (!isSentList && !isDraftList) {
                    setEmails((prev) =>
                      prev.map((e) =>
                        e.id === email.id ? { ...e, is_read: true } : e
                      )
                    );
                  }
                }}
              >
                {/* ドラフト用表示 */}
                {isDraftList ? (
                  <>
                    <strong>{email.subject || "(件名なし)"}</strong>
                    <div className="MailList-meta">
                      <span>{email.to}</span>
                      <span>{new Date(email.created_at).toLocaleString()}</span>
                    </div>
                  </>
                ) : (
                  <>
                    {/* ● ステータスドット（未読のみ） */}
                    {!isSentList && (
                      <span
                        className="MailList-statusDot"
                        title={email.is_read ? "既読（クリックで未読に）" : "未読"}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (email.is_read) {
                            await apiRequest(
                              `inventory/mail/received/${email.id}/mark_unread/`,
                              "PATCH"
                            );
                            setEmails((prev) =>
                              prev.map((e) =>
                                e.id === email.id ? { ...e, is_read: false } : e
                              )
                            );
                            if (onReload) onReload();
                          }
                        }}
                      />
                    )}
                    <strong>{email.subject}</strong>
                    <div className="MailList-meta">
                      <span>{isSentList ? email.to_address : email.from_address}</span>
                      <span>
                        {new Date(isSentList ? email.sent_at : email.received_at).toLocaleString()}
                      </span>
                    </div>
                  </>
                )}
              </li>

            );
          })}
        </ul>
        {loading && <div className="mail-loading">読み込み中...</div>}
        {!hasMore && <div className="mail-end">これ以上はありません</div>}
      </div>
    </div>
  );
};

export default MailList;
